import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';

interface QuickStartContextType {
  isOpen: boolean;
  openQuickStart: () => void;
  closeQuickStart: () => void;
}

const QuickStartContext = createContext<QuickStartContextType | undefined>(undefined);

export const useQuickStart = () => {
  const context = useContext(QuickStartContext);
  if (!context) {
    throw new Error('useQuickStart must be used within QuickStartProvider');
  }
  return context;
};

interface QuickStartProviderProps {
  children: ReactNode;
}

/**
 * Holds the Quick Start modal's open/close state so it's reachable both from
 * the header button and anywhere else in the app without prop-drilling
 * through App. The modal renders the SAME assistant thread as the chat
 * bubble (see ChatSupportContext) — this context only controls which chrome
 * (docked bubble vs. large centered modal) is currently visible.
 */
export const QuickStartProvider: React.FC<QuickStartProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const openQuickStart = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeQuickStart = useCallback(() => {
    setIsOpen(false);
  }, []);

  const value: QuickStartContextType = {
    isOpen,
    openQuickStart,
    closeQuickStart,
  };

  return <QuickStartContext.Provider value={value}>{children}</QuickStartContext.Provider>;
};

export default QuickStartContext;
